import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { stripeApi } from '@/services/api/stripeApi';
import { restaurantApi } from '@/services/api/restaurantApi';
import { authApi } from '@/services/api/authApi';
import Logo from '@/components/Logo';
import { getApiUrl } from '@/config/api';
import Cookies from 'js-cookie';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sessionDetails, setSessionDetails] = useState<any>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);

  useEffect(() => {
    // ======================================================================
    // ACTIVATION FLOW CLEANUP (Sales Portal Pre-Onboarding)
    // ======================================================================
    // Check if this was an activation flow and clean up tokens
    const activationToken = localStorage.getItem('activation_session_token');
    const planSelectionToken = localStorage.getItem('plan_selection_session_token');

    if (activationToken || planSelectionToken) {
      console.log('🎬 ACTIVATION FLOW: Cleaning up activation tokens');
      // Clear activation tokens after successful payment
      localStorage.removeItem('activation_session_token');
      localStorage.removeItem('plan_selection_session_token');
      localStorage.removeItem('activation_rest_id');
      localStorage.removeItem('activation_restaurant_name');
      localStorage.removeItem('activation_oauth_provider');
      console.log('✅ Activation tokens cleared - account is now active');
    }
    // ======================================================================

    const sessionId = searchParams.get('session_id');

    if (sessionId) {
      // Poll for subscription with retry logic
      // Webhook may take a few seconds to process and create subscription
      let attempts = 0;
      const maxAttempts = 10; // 10 attempts = 20 seconds max
      const pollInterval = 2000; // 2 seconds between attempts

      const checkSubscription = async () => {
        try {
          console.log(`🔄 Polling for subscription (attempt ${attempts + 1}/${maxAttempts})...`);

          const [sessionData, profileData, subData] = await Promise.all([
            stripeApi.getCheckoutSession(sessionId),
            restaurantApi.getProfile(), // Get user profile for email
            fetch(getApiUrl('subscription/current'), {
              headers: {
                'Authorization': `Bearer ${Cookies.get('auth_token')}`,
              }
            }).then(res => res.json())
          ]);

          // Check if subscription was created
          if (subData && subData.subscription_id) {
            // Subscription found!
            console.log('✅ Subscription found:', subData);
            setSessionDetails(sessionData);
            setSubscriptionInfo(subData);
            setUserEmail(profileData.email || sessionData.customer_email || '');
            setLoading(false);

            console.log('🔍 Subscription data received:', {
              is_trial: subData?.is_trial,
              subscription_status: subData?.subscription_status,
              plan_name: subData?.plan_name
            });

            const isUpgrade = searchParams.get('upgrade') === 'true';
            const hasTrial = (subData?.plan_name?.toLowerCase() === 'delicious') &&
                            (subData?.is_trial === true || subData?.subscription_status === 'trialing');

            toast({
              title: isUpgrade ? "Upgrade Successful!" : "Welcome to Dine AI!",
              description: isUpgrade
                ? "Your plan has been upgraded. You can now access all new features!"
                : hasTrial
                  ? "Your 30-day free trial has started. Let's set up your restaurant!"
                  : "Your subscription is active. Let's set up your restaurant!",
            });
          } else if (attempts < maxAttempts) {
            // Subscription not found yet, retry
            attempts++;
            console.log(`⏳ Subscription not ready, retrying in ${pollInterval/1000}s...`);
            setTimeout(checkSubscription, pollInterval);
          } else {
            // Max attempts reached
            console.warn('⚠️ Subscription not found after max attempts');
            setLoading(false);
            toast({
              title: "Payment Processing",
              description: "Your payment was successful! Your subscription is being activated and will be ready shortly.",
            });

            // Still set some basic info so page isn't totally empty
            setSessionDetails(sessionData);
            setUserEmail(profileData.email || sessionData.customer_email || '');
          }
        } catch (error) {
          console.error('Error verifying session:', error);

          if (attempts < maxAttempts) {
            // Retry on error
            attempts++;
            setTimeout(checkSubscription, pollInterval);
          } else {
            setLoading(false);
            toast({
              title: "Error",
              description: "Unable to verify payment. Please contact support.",
              variant: "destructive"
            });
          }
        }
      };

      checkSubscription();
    } else {
      setLoading(false);
    }
  }, [searchParams, toast]);

  const handleContinue = async () => {
    // Refresh JWT token to reflect current database state (subscription status)
    // This is critical because the JWT was generated before payment completion
    try {
      console.log('🔄 Refreshing JWT token with current subscription status...');
      await authApi.refreshContext();
      console.log('✅ JWT token refreshed successfully');
    } catch (error) {
      console.error('❌ Failed to refresh JWT token:', error);
      // Continue anyway - the user has paid, don't block them
    }

    // Check if this is an upgrade
    const isUpgrade = searchParams.get('upgrade') === 'true';
    // Check if this is an upsell (adding a second product to existing customer)
    const isUpsell = searchParams.get('upsell') === 'true';

    if (isUpgrade || isUpsell) {
      // For upgrades and upsells, force full page reload to refresh auth token
      // User already has a restaurant and menu, go straight to dashboard
      console.log(`🎯 ${isUpsell ? 'Upsell' : 'Upgrade'} complete - redirecting to dashboard`);
      window.location.href = '/restaurant-dashboard';
      return;
    }

    // Check backend for any unclaimed extractions
    console.log('🎯 PaymentSuccess - Checking backend for unclaimed extractions...');

    try {
      // Get restaurant profile to get restaurant name/address
      const profileResponse = await restaurantApi.getProfile();

      if (profileResponse.restaurant_name) {
        // Check for unclaimed extractions
        const checkResponse = await fetch(getApiUrl('frictionless-onboarding/check-unclaimed-extraction'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${Cookies.get('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            restaurant_name: profileResponse.restaurant_name,
            restaurant_address: profileResponse.address,
            restaurant_phone: profileResponse.phone
          })
        });
        
        if (checkResponse.ok) {
          const extractionData = await checkResponse.json();
          
          if (extractionData.found) {
            console.log('✅ Found unclaimed extraction:', extractionData);
            
            // Store extraction data for claim page
            sessionStorage.setItem('preview_session_token', extractionData.session_token);
            sessionStorage.setItem('extraction_completed', 'true');
            sessionStorage.setItem('preview_items_extracted', String(extractionData.items_count));
            sessionStorage.setItem('preview_restaurant_name', extractionData.restaurant_name);
            
            // Navigate to claim page
            navigate('/restaurant-menu-claim');
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error checking for unclaimed extractions:', error);
    }

    // SIMPLIFIED: After payment, restaurant already exists from onboarding
    // User just needs to claim their menu or upload one
    console.log('🎯 PaymentSuccess - Checking localStorage for extraction data...');
    
    // Check ALL possible sources of extraction data
    const localStorage_pending = localStorage.getItem('pending_extraction');
    const localStorage_token = localStorage.getItem('extraction_session_token');
    const localStorage_items = localStorage.getItem('extraction_items_count');
    const sessionStorage_token = sessionStorage.getItem('preview_session_token');
    const sessionStorage_completed = sessionStorage.getItem('extraction_completed');
    const sessionStorage_items = sessionStorage.getItem('preview_items_extracted');
    
    console.log('📦 Complete Storage Check:', {
      localStorage: {
        pending_extraction: localStorage_pending,
        extraction_session_token: localStorage_token ? 'EXISTS' : 'NULL',
        extraction_items_count: localStorage_items
      },
      sessionStorage: {
        preview_session_token: sessionStorage_token ? 'EXISTS' : 'NULL',
        extraction_completed: sessionStorage_completed,
        preview_items_extracted: sessionStorage_items
      },
      timestamp: new Date().toISOString()
    });
    
    // Try to restore from localStorage if present
    if (localStorage_pending === 'true' && localStorage_token) {
      console.log('📥 Restoring extraction data from localStorage...');
      sessionStorage.setItem('preview_session_token', localStorage_token);
      sessionStorage.setItem('extraction_completed', 'true');
      if (localStorage_items) {
        sessionStorage.setItem('preview_items_extracted', localStorage_items);
      }
      
      // Clean up localStorage after restoration
      localStorage.removeItem('pending_extraction');
      localStorage.removeItem('extraction_session_token');
      localStorage.removeItem('extraction_items_count');
      console.log('✅ Extraction data restored and localStorage cleaned');
    }
    
    // Now check if we have extraction data from ANY source
    const hasExtraction = (
      sessionStorage.getItem('extraction_completed') === 'true' || 
      sessionStorage.getItem('preview_session_token') !== null
    );
    
    console.log('🔍 Final Extraction Check:', {
      hasExtraction,
      token: sessionStorage.getItem('preview_session_token') ? 'EXISTS' : 'NULL',
      completed: sessionStorage.getItem('extraction_completed'),
      items: sessionStorage.getItem('preview_items_extracted')
    });
    
    if (hasExtraction) {
      // User has menu extraction to claim - go to dedicated claim page
      console.log('✅ Extraction data detected - navigating to menu CLAIM page');
      navigate('/restaurant-menu-claim');
    } else {
      // No extraction data - user needs to upload a menu
      console.log('❌ No extraction data - navigating to menu UPLOAD page');
      navigate('/restaurant-menu-upload');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-white text-xl">Verifying your subscription...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Logo className="text-white h-10" />
          </div>

          {/* Success Card */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
            </div>

            <h1 className="text-3xl font-bold text-white mb-4">
              {searchParams.get('upgrade') === 'true'
                ? 'Upgrade Successful! 🎉'
                : searchParams.get('upsell') === 'true'
                  ? 'Product Added Successfully! 🎉'
                  : 'Welcome to Dine AI! 🎉'}
            </h1>

            <p className="text-xl text-white/80 mb-8">
              {searchParams.get('upgrade') === 'true'
                ? 'Your plan has been upgraded successfully.'
                : searchParams.get('upsell') === 'true'
                  ? 'Your new product has been activated successfully.'
                  : (subscriptionInfo?.plan_name?.toLowerCase() === 'delicious' &&
                     (subscriptionInfo?.is_trial === true || subscriptionInfo?.subscription_status === 'trialing'))
                    ? 'Your 30-day free trial has started successfully.'
                    : 'Your subscription has been activated successfully.'}
            </p>

            {sessionDetails && (
              <div className="bg-white/5 rounded-lg p-6 mb-8 text-left">
                <h3 className="text-lg font-semibold text-white mb-3">
                  {searchParams.get('upsell') === 'true' ? 'New Product Details:' : 'Subscription Details:'}
                </h3>
                <div className="space-y-2 text-white/70">
                  <p>Email: {userEmail}</p>
                  <p>Plan: {searchParams.get('upsell') === 'true'
                    ? (sessionDetails?.metadata?.plan_name?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Virtual Host')
                    : (subscriptionInfo?.plan_display_name || subscriptionInfo?.plan_name || 'Loading...')}</p>
                  <p>Product: {searchParams.get('upsell') === 'true'
                    ? (sessionDetails?.metadata?.product_type === 'virtual_host' ? 'AI Virtual Host' : 'AI Virtual Assistant')
                    : (subscriptionInfo?.product_type === 'virtual_host' ? 'AI Virtual Host' : 'AI Virtual Assistant')}</p>
                  <p>Status: {searchParams.get('upgrade') === 'true'
                    ? 'Upgraded Successfully'
                    : searchParams.get('upsell') === 'true'
                      ? 'Active Subscription'
                      : (subscriptionInfo?.plan_name?.toLowerCase() === 'delicious' &&
                         (subscriptionInfo?.is_trial === true || subscriptionInfo?.subscription_status === 'trialing'))
                        ? 'Active Trial (30 days)'
                        : 'Active Subscription'}</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Button
                onClick={handleContinue}
                className="w-full py-6 text-lg bg-[#FDB913] hover:bg-[#FDB913]/90 text-black font-semibold"
              >
                {searchParams.get('upgrade') === 'true' || searchParams.get('upsell') === 'true'
                  ? 'Go to Dashboard'
                  : 'Continue to Setup'}
              </Button>

              {searchParams.get('upgrade') !== 'true' &&
               subscriptionInfo?.plan_name?.toLowerCase() === 'delicious' &&
               (subscriptionInfo?.is_trial === true || subscriptionInfo?.subscription_status === 'trialing') && (
                <p className="text-sm text-white/60">
                  You won't be charged during your trial period. Cancel anytime.
                </p>
              )}
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center text-white/60 text-sm">
            <p>Need help? Contact us at support@dineai.ai</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;